import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import {
  AdminBooleanToggleField,
  AdminRelatedLinks,
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanAdminOperatorAccessDetail,
  fetchYowanAdminOperatorMailSenderIdentity,
  getYowanApiFieldErrorMessage,
  updateYowanAdminOperatorAccess,
  updateYowanAdminOperatorMailSenderIdentity,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const buildOperatorAccessFormValues = (record = {}) => ({
  operatorRoles: record.operatorRoles?.length ? record.operatorRoles : ['admin'],
  isActive: record.isActive !== false,
});

const splitMailAddress = (value = '', fallbackDomains = []) => {
  const text = String(value || '').trim();
  const atIndex = text.lastIndexOf('@');
  const fallbackDomain = fallbackDomains[0] || '';

  if (atIndex < 0) {
    return {
      localPart: text,
      domain: fallbackDomain,
    };
  }

  return {
    localPart: text.slice(0, atIndex),
    domain: text.slice(atIndex + 1) || fallbackDomain,
  };
};

const buildMailAddress = (localPart = '', domain = '') => {
  const trimmedLocalPart = String(localPart || '').trim();
  const trimmedDomain = String(domain || '').trim();

  if (!trimmedLocalPart) {
    return '';
  }

  return trimmedDomain ? `${trimmedLocalPart}@${trimmedDomain}` : trimmedLocalPart;
};

const normalizeMailLocalPartInput = (value = '') => String(value || '').split('@')[0].trim();

const buildMailSenderIdentityFormValues = (payload = {}, record = {}) => {
  const identity = payload.identity || record.mailSenderIdentity || {};
  const senderDomains = payload.allowedSenderDomains || [];
  const replyToDomains = payload.allowedReplyToDomains || [];
  const emailParts = splitMailAddress(identity.email, senderDomains);
  const replyToEmailParts = splitMailAddress(identity.replyToEmail, replyToDomains);

  return {
    label: identity.label || '',
    displayName: identity.displayName || record.accountName || '',
    emailLocalPart: emailParts.localPart,
    emailDomain: emailParts.domain,
    replyToLocalPart: replyToEmailParts.localPart,
    replyToDomain: replyToEmailParts.domain,
    isActive: identity.id ? identity.isActive !== false : true,
    isDefault: identity.id ? identity.isDefault !== false : true,
  };
};

const buildMailSenderIdentitySubmitValues = (values = {}) => ({
  ...values,
  email: buildMailAddress(values.emailLocalPart, values.emailDomain),
  replyToEmail: buildMailAddress(values.replyToLocalPart, values.replyToDomain),
});

const getMailSenderIdentityErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data || {};
  const fieldMessage = getYowanApiFieldErrorMessage({ response: { data: responseData } }, [
    'label',
    'display_name',
    'email',
    'reply_to_email',
    'is_active',
    'is_default',
    'non_field_errors',
    'detail',
  ]);

  return fieldMessage ? `${fallbackMessage} ${fieldMessage}` : fallbackMessage;
};

const AdminOperatorAccessEditPage = () => {
  const content = yowanPageContent['/admin/access/operator/:accessId/edit'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const navigate = useNavigate();
  const { accessId } = useParams();
  const [currentRecord, setCurrentRecord] = useState(null);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mailSenderState, setMailSenderState] = useState({
    identity: null,
    allowedSenderDomains: [],
    allowedReplyToDomains: [],
  });
  const [mailSenderValues, setMailSenderValues] = useState(buildMailSenderIdentityFormValues());
  const [mailSenderMessage, setMailSenderMessage] = useState('');
  const [mailSenderLoadError, setMailSenderLoadError] = useState('');
  const [isMailSenderLoading, setIsMailSenderLoading] = useState(false);
  const [isMailSenderSubmitting, setIsMailSenderSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: currentRecord ? buildOperatorAccessFormValues(currentRecord) : {
      operatorRoles: ['admin'],
      isActive: true,
    },
  });

  useEffect(() => {
    if (!content || !accessId) {
      return undefined;
    }

    let isMounted = true;

    const loadAccess = async () => {
      setIsLoading(true);
      setIsMailSenderLoading(true);
      setLoadError('');
      setMailSenderLoadError('');

      try {
        const [record, mailSenderPayload] = await Promise.all([
          fetchYowanAdminOperatorAccessDetail(accessId),
          fetchYowanAdminOperatorMailSenderIdentity(accessId).catch(() => {
            if (!isMounted) {
              return null;
            }

            setMailSenderLoadError(content.form.mailSenderIdentity.loadErrorMessage);
            return null;
          }),
        ]);

        if (!isMounted) {
          return;
        }

        setCurrentRecord(record);
        reset(buildOperatorAccessFormValues(record));
        if (mailSenderPayload) {
          setMailSenderState(mailSenderPayload);
          setMailSenderValues(buildMailSenderIdentityFormValues(mailSenderPayload, record));
          setMailSenderLoadError('');
        } else {
          setMailSenderState({
            identity: record.mailSenderIdentity || null,
            allowedSenderDomains: [],
            allowedReplyToDomains: [],
          });
          setMailSenderValues(buildMailSenderIdentityFormValues({ identity: record.mailSenderIdentity }, record));
        }
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setCurrentRecord(null);
        setLoadError(content.form.loadErrorMessage);
      } finally {
        if (isMounted) {
          setIsLoading(false);
          setIsMailSenderLoading(false);
        }
      }
    };

    loadAccess();

    return () => {
      isMounted = false;
    };
  }, [accessId, content, reset]);

  if (!content) {
    return null;
  }

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitMessage(content.form.pendingMessage);

    try {
      await updateYowanAdminOperatorAccess(accessId, values);
      setSubmitMessage(content.form.successMessage);
      navigate(appendYowanPreviewQueryIfNeeded('/admin/access/operator', preservedDomainSearch));
    } catch (error) {
      setSubmitMessage(content.form.errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateMailSenderValue = (name) => (event) => {
    const rawValue = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    const value = name === 'emailLocalPart' || name === 'replyToLocalPart'
      ? normalizeMailLocalPartInput(rawValue)
      : rawValue;

    setMailSenderValues((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const onMailSenderSubmit = async (event) => {
    event.preventDefault();
    setIsMailSenderSubmitting(true);
    setMailSenderMessage(content.form.mailSenderIdentity.pendingMessage);

    try {
      const payload = await updateYowanAdminOperatorMailSenderIdentity(
        accessId,
        buildMailSenderIdentitySubmitValues(mailSenderValues),
      );
      setMailSenderState(payload);
      setMailSenderValues(buildMailSenderIdentityFormValues(payload, currentRecord || {}));
      setMailSenderMessage(content.form.mailSenderIdentity.successMessage);
      setMailSenderLoadError('');
    } catch (error) {
      setMailSenderMessage(getMailSenderIdentityErrorMessage(
        error,
        content.form.mailSenderIdentity.errorMessage,
      ));
    } finally {
      setIsMailSenderSubmitting(false);
    }
  };

  const targetLabel = currentRecord
    ? [currentRecord.accountName || 'プロフィール未設定', currentRecord.accountEmail].filter(Boolean).join(' / ')
    : '';
  const senderDomains = mailSenderState.allowedSenderDomains || [];
  const replyToDomains = mailSenderState.allowedReplyToDomains || [];
  const requireAtLeastOne = (value) => (
    Array.isArray(value) && value.length > 0
      ? true
      : content.form.errors.selectAtLeastOne || content.form.errors.required
  );

  return (
    <AdminBlogShell activeNavHref="/admin/access/operator" routeKey="/admin/access/operator/:accessId/edit">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanPreviewQueryIfNeeded('/admin/access/operator', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
        ]}
      />
      <AdminRelatedLinks
        links={content.relatedLinks}
        preservedDomainSearch={preservedDomainSearch}
      />

      {isLoading || loadError || !currentRecord ? (
        <div className="yowan-admin-form c-surface-content">
          {isLoading ? <LoadingSpinner size="sm" /> : loadError || content.form.loadErrorMessage}
        </div>
      ) : (
        <>
          <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
            <div className="c-form__field">
              <span className="c-form__label">{content.form.labels.operator}</span>
              <input className="c-form__control" type="text" value={targetLabel || '-'} disabled readOnly />
            </div>

            {(content.form.accessBindings || []).map((binding) => (
              <div key={binding.name} className="c-form__field">
                <span className="c-form__label">{binding.label}</span>
                <div className="yowan-admin-form__choice-group">
                  {binding.options.map((option) => (
                    <label key={option.value} className="c-form__choice yowan-admin-form__toggle-choice">
                      <input
                        type="checkbox"
                        value={option.value}
                        {...register(binding.name, { validate: requireAtLeastOne })}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
                {errors?.[binding.name] ? (
                  <em
                    className="yowan-admin-form__error"
                  >
                    {errors[binding.name].message}
                  </em>
                ) : null}
                {binding.helpText ? (
                  <p
                    className="yowan-admin-form__field-note yowan-admin-form__field-note--compact"
                  >
                    {binding.helpText}
                  </p>
                ) : null}
              </div>
            ))}

            <AdminBooleanToggleField
              label={content.form.labels.isActive}
              name="isActive"
              register={register}
              helpText={content.form.isActiveHelpText}
            />

            <div className="yowan-admin-form__actions">
              <button type="submit" className="c-form__submit" disabled={isSubmitting}>
                {isSubmitting ? content.form.pendingMessage : content.form.submitLabel}
              </button>
            </div>

            {submitMessage ? <p className="yowan-admin-form__message">{submitMessage}</p> : null}
          </form>

          <form className="yowan-admin-form c-surface-content yowan-admin-mail-sender-panel" onSubmit={onMailSenderSubmit}>
            <div className="yowan-admin-mail-sender-panel__header">
              <div>
                <h2>{content.form.mailSenderIdentity.title}</h2>
                <p>{content.form.mailSenderIdentity.lead}</p>
              </div>
              {mailSenderState.identity?.id ? (
                <span className="yowan-admin-resource-table__status yowan-admin-resource-table__status--published">
                  設定済み
                </span>
              ) : (
                <span className="yowan-admin-resource-table__status yowan-admin-resource-table__status--review">
                  未設定
                </span>
              )}
            </div>

            {mailSenderLoadError ? (
              <p className="yowan-admin-form__error">{mailSenderLoadError}</p>
            ) : null}

            <div className="yowan-admin-mail-sender-panel__domains">
              <p>
                <span>{content.form.mailSenderIdentity.allowedSenderDomainsLabel}</span>
                {(mailSenderState.allowedSenderDomains || []).join(' / ') || '-'}
              </p>
              <p>
                <span>{content.form.mailSenderIdentity.allowedReplyToDomainsLabel}</span>
                {(mailSenderState.allowedReplyToDomains || []).join(' / ') || '-'}
              </p>
            </div>

            <label className="c-form__field">
              <span className="c-form__label">{content.form.mailSenderIdentity.labels.label}</span>
              <input
                className="c-form__control"
                value={mailSenderValues.label}
                onChange={updateMailSenderValue('label')}
                disabled={isMailSenderSubmitting}
              />
            </label>

            <label className="c-form__field">
              <span className="c-form__label">{content.form.mailSenderIdentity.labels.displayName}</span>
              <input
                className="c-form__control"
                value={mailSenderValues.displayName}
                onChange={updateMailSenderValue('displayName')}
                disabled={isMailSenderSubmitting}
              />
            </label>

            <label className="c-form__field">
              <span className="c-form__label">{content.form.mailSenderIdentity.labels.email}</span>
              <div className="yowan-admin-mail-sender-panel__address-control">
                <input
                  className="c-form__control"
                  value={mailSenderValues.emailLocalPart}
                  onChange={updateMailSenderValue('emailLocalPart')}
                  disabled={isMailSenderSubmitting}
                  required
                />
                {senderDomains.length > 1 ? (
                  <select
                    className="c-form__control"
                    value={mailSenderValues.emailDomain}
                    onChange={updateMailSenderValue('emailDomain')}
                    disabled={isMailSenderSubmitting}
                    required
                  >
                    {senderDomains.map((domain) => (
                      <option key={domain} value={domain}>
                        @{domain}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="yowan-admin-mail-sender-panel__domain-suffix">
                    @{mailSenderValues.emailDomain || senderDomains[0] || '-'}
                  </span>
                )}
              </div>
              <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">
                {content.form.mailSenderIdentity.emailHelpText}
              </p>
            </label>

            <label className="c-form__field">
              <span className="c-form__label">{content.form.mailSenderIdentity.labels.replyToEmail}</span>
              <div className="yowan-admin-mail-sender-panel__address-control">
                <input
                  className="c-form__control"
                  value={mailSenderValues.replyToLocalPart}
                  onChange={updateMailSenderValue('replyToLocalPart')}
                  disabled={isMailSenderSubmitting}
                />
                {replyToDomains.length > 1 ? (
                  <select
                    className="c-form__control"
                    value={mailSenderValues.replyToDomain}
                    onChange={updateMailSenderValue('replyToDomain')}
                    disabled={isMailSenderSubmitting || !mailSenderValues.replyToLocalPart}
                  >
                    {replyToDomains.map((domain) => (
                      <option key={domain} value={domain}>
                        @{domain}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="yowan-admin-mail-sender-panel__domain-suffix">
                    @{mailSenderValues.replyToDomain || replyToDomains[0] || '-'}
                  </span>
                )}
              </div>
              <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">
                {content.form.mailSenderIdentity.replyToHelpText}
              </p>
            </label>

            <div className="yowan-admin-form__choice-group">
              <label className="c-form__choice yowan-admin-form__toggle-choice">
                <input
                  type="checkbox"
                  checked={mailSenderValues.isActive}
                  onChange={updateMailSenderValue('isActive')}
                  disabled={isMailSenderSubmitting}
                />
                <span>{content.form.mailSenderIdentity.labels.isActive}</span>
              </label>
              <label className="c-form__choice yowan-admin-form__toggle-choice">
                <input
                  type="checkbox"
                  checked={mailSenderValues.isDefault}
                  onChange={updateMailSenderValue('isDefault')}
                  disabled={isMailSenderSubmitting}
                />
                <span>{content.form.mailSenderIdentity.labels.isDefault}</span>
              </label>
            </div>

            <div className="yowan-admin-form__actions">
              <button type="submit" className="c-form__submit" disabled={isMailSenderSubmitting || isMailSenderLoading}>
                {isMailSenderSubmitting
                  ? content.form.mailSenderIdentity.pendingMessage
                  : content.form.mailSenderIdentity.submitLabel}
              </button>
              {isMailSenderSubmitting ? <LoadingSpinner size="sm" /> : null}
            </div>

            {mailSenderMessage ? <p className="yowan-admin-form__message">{mailSenderMessage}</p> : null}
          </form>
        </>
      )}
    </AdminBlogShell>
  );
};

export default AdminOperatorAccessEditPage;
